import { useRef, useState } from 'react'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Btn from '../Btn'
import AvatarEditor from '../AvatarEditor'

import { AVATAR_TYPES, pruefeAvatarDatei } from '../../shared/avatarFile'

import photoIcon from '../../assets/icons/photo.svg'

import styles from './style.module.scss'

/**
 * Profilbild wählen, zurechtschneiden und übergeben.
 *
 * Ein Baustein für alle vier Rollen. Vorher hatten Schüler:innen und Lehrkräfte
 * diesen hier, die Unterstützer:innen ein eigenes Feld, und die Verwaltung gar
 * nichts — mit drei verschiedenen Antworten auf dieselben Fragen (welche
 * Formate, wie groß, was passiert bei einer falschen Datei).
 *
 * Zwischen Auswahl und Übergabe liegt jetzt das Fenster zum Zuschneiden. Wer es
 * abbricht, hat nichts hochgeladen: `onSelect` wird erst danach gerufen.
 */
export default function AvatarUploader({
	src,
	name,
	className,
	disabled = false,
	error,
	size = 80,
	round = true,
	onSelect,
	onClean = () => {}
}) {
	const { t } = useTranslation()
	const inputRef = useRef(null)
	const [gewaehlt, setGewaehlt] = useState(null)
	const [meldung, setMeldung] = useState(null)

	const waehlen = event => {
		const file = event.target.files?.[0]
		// Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann — sonst
		// bleibt das Feld nach einem Abbruch stumm.
		event.target.value = ''
		if (!file) return

		// Die Prüfung gibt einen Schlüssel zurück, keinen fertigen Satz.
		const fehler = pruefeAvatarDatei(file)
		setMeldung(fehler ? t(fehler) : null)
		if (fehler) return

		setGewaehlt(file)
	}

	const uebernehmen = zugeschnitten => {
		setGewaehlt(null)
		onSelect(zugeschnitten)
	}

	const text = error?.message || meldung

	return (
		<div className={clsx(styles.root, className)}>
			{src ? (
				<Avatar src={src} className={styles.preview} color="#e5eaf5" round={round} size={String(size)} />
			) : (
				<Avatar
					name={name || '?'}
					className={styles.preview}
					color="#e5eaf5"
					round={round}
					size={String(size)}
				/>
			)}

			<div className={styles.wrapper}>
				{text && <span className={styles.error}>{text}</span>}
				<div className={styles.wrapperInput}>
					<label className={clsx(styles.labelUploadFile, disabled && styles.labelUploadFileDisabled)}>
						<input
							ref={inputRef}
							disabled={disabled}
							className={styles.input}
							type="file"
							accept={AVATAR_TYPES.join(',')}
							onChange={waehlen}
						/>
						<img className={styles.labelUploadFileIcon} src={photoIcon} alt="" />
						<span>{src ? t('changePicture') : t('Upload profile photo')}</span>
					</label>
					{src && (
						<Btn
							disabled={disabled}
							className={styles.btnRemove}
							onClick={onClean}
							icon="delete"
							iconColor="#008B9C"
							theme="white"
							tooltip="delete"
						/>
					)}
				</div>
				<span className={styles.hint}>{t('avatarHint')}</span>
			</div>

			{gewaehlt && (
				<AvatarEditor file={gewaehlt} round={round} onCancel={() => setGewaehlt(null)} onDone={uebernehmen} />
			)}
		</div>
	)
}

AvatarUploader.propTypes = {
	/** Lokale Adresse des gespeicherten Bildes (siehe `shared/useAvatarUrls`). */
	src: PropTypes.string,
	/** Für die Initialen, solange kein Bild da ist. */
	name: PropTypes.string,
	className: PropTypes.string,
	disabled: PropTypes.bool,
	/** Meldung von außen, etwa nach einem gescheiterten Hochladen. */
	error: PropTypes.object,
	size: PropTypes.number,
	round: PropTypes.bool,
	/** Bekommt die zugeschnittene Datei. */
	onSelect: PropTypes.func.isRequired,
	onClean: PropTypes.func
}
